import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from "react";
import { Button, Card, DataTable, Field, Modal } from "../../components/index.js";
import type { Column } from "../../components/index.js";
import {
  emptyCadastroForm,
  validateCadastroForm,
  type CadastroFormErrors,
  type CadastroFormValues,
  type CadastroKind
} from "../../lib/cadastro-form.js";
import { SkuTableEditor } from "./SkuTableEditor.js";

/**
 * Tela genérica de cadastro com tabela de SKUs — base de Fornecedores (§8.2) e
 * Prep Center (§8.3), que diferem só no campo "extra" (Lead Time vs Telefone) e
 * nas rotas de API. Lista + modal de criação/edição. Mutações são ADMIN-only no
 * backend; quando `canMutate` é falso, a tela fica somente-leitura (decisão 5).
 */
export interface CadastroComSkusProps<TEntity> {
  kind: CadastroKind;
  title: string;
  subtitle: string;
  newLabel: string;
  extraLabel: string;
  extraColumnHeader: string;
  extraInputType?: string;
  extraPlaceholder?: string;
  canMutate: boolean;
  list: () => Promise<TEntity[]>;
  save: (id: string | null, values: CadastroFormValues) => Promise<void>;
  remove: (id: string) => Promise<void>;
  getId: (entity: TEntity) => string;
  getName: (entity: TEntity) => string;
  getAddress: (entity: TEntity) => string;
  getExtraDisplay: (entity: TEntity) => ReactNode;
  getSkuCount: (entity: TEntity) => number;
  toForm: (entity: TEntity) => CadastroFormValues;
}

interface EditState {
  id: string | null;
  values: CadastroFormValues;
}

export function CadastroComSkus<TEntity>(
  props: CadastroComSkusProps<TEntity>
): ReactNode {
  const { kind, list, save, remove, getId } = props;

  const [entities, setEntities] = useState<TEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const reqSeq = useRef(0);

  const [edit, setEdit] = useState<EditState | null>(null);
  const [formErrors, setFormErrors] = useState<CadastroFormErrors>({ skus: [] });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [removeTarget, setRemoveTarget] = useState<TEntity | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setErro(null);
    try {
      const result = await list();
      if (seq !== reqSeq.current) return;
      setEntities(result);
    } catch (error: unknown) {
      if (seq !== reqSeq.current) return;
      setErro(error instanceof Error ? error.message : "Falha ao carregar os dados.");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    void load();
  }, [load]);

  function openNew(): void {
    setFormErrors({ skus: [] });
    setFormError(null);
    setEdit({ id: null, values: emptyCadastroForm() });
  }

  function openEdit(entity: TEntity): void {
    setFormErrors({ skus: [] });
    setFormError(null);
    setEdit({ id: getId(entity), values: props.toForm(entity) });
  }

  async function handleSave(): Promise<void> {
    if (edit === null) return;
    const validation = validateCadastroForm(kind, edit.values);
    setFormErrors(validation.errors);
    if (!validation.valid) {
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      await save(edit.id, edit.values);
      setEdit(null);
      await load();
    } catch (error: unknown) {
      setFormError(
        error instanceof Error ? error.message : "Falha ao salvar o cadastro."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(): Promise<void> {
    if (removeTarget === null) return;
    setRemoving(true);
    try {
      await remove(getId(removeTarget));
      setRemoveTarget(null);
      await load();
    } catch (error: unknown) {
      setRemoveTarget(null);
      setErro(error instanceof Error ? error.message : "Falha ao remover o cadastro.");
    } finally {
      setRemoving(false);
    }
  }

  const columns: readonly Column<TEntity>[] = [
    { key: "name", header: "Nome", render: (e) => props.getName(e) },
    { key: "address", header: "Endereço", render: (e) => props.getAddress(e) },
    {
      key: "extra",
      header: props.extraColumnHeader,
      render: (e) => props.getExtraDisplay(e)
    },
    {
      key: "skus",
      header: "SKUs",
      align: "right",
      render: (e) => props.getSkuCount(e)
    },
    ...(props.canMutate
      ? [
          {
            key: "actions",
            header: "Ações",
            align: "right" as const,
            render: (e: TEntity) => (
              <div style={{ display: "inline-flex", gap: "0.4rem" }}>
                <Button variant="secondary" onClick={() => openEdit(e)}>
                  Editar
                </Button>
                <Button variant="secondary" onClick={() => setRemoveTarget(e)}>
                  Remover
                </Button>
              </div>
            )
          }
        ]
      : [])
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap"
        }}
      >
        <div>
          <h2 style={{ fontSize: 21, fontWeight: 700 }}>{props.title}</h2>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
            {props.subtitle}
          </p>
        </div>
        {props.canMutate ? (
          <Button onClick={openNew}>{props.newLabel}</Button>
        ) : null}
      </header>

      <Card>
        {erro !== null ? (
          <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, marginBottom: "0.5rem" }}>
            {erro}
          </p>
        ) : null}
        <DataTable
          columns={columns}
          rows={entities}
          getRowKey={getId}
          empty={loading ? "Carregando…" : "Nenhum cadastro."}
        />
      </Card>

      <Modal
        open={edit !== null}
        onClose={() => setEdit(null)}
        title={edit?.id === null ? props.newLabel : "Editar cadastro"}
        width={680}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEdit(null)}>
              Cancelar
            </Button>
            <Button disabled={saving} onClick={() => void handleSave()}>
              {saving ? "Salvando…" : "Salvar"}
            </Button>
          </>
        }
      >
        {edit !== null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            <Field
              label="Nome"
              required
              {...(formErrors.name !== undefined ? { error: formErrors.name } : {})}
            >
              <input
                value={edit.values.name}
                disabled={saving}
                onChange={(e) =>
                  setEdit({ ...edit, values: { ...edit.values, name: e.target.value } })
                }
              />
            </Field>
            <Field
              label="Endereço"
              required
              {...(formErrors.address !== undefined ? { error: formErrors.address } : {})}
            >
              <input
                value={edit.values.address}
                disabled={saving}
                onChange={(e) =>
                  setEdit({
                    ...edit,
                    values: { ...edit.values, address: e.target.value }
                  })
                }
              />
            </Field>
            <Field
              label={props.extraLabel}
              required
              {...(formErrors.extra !== undefined ? { error: formErrors.extra } : {})}
            >
              <input
                type={props.extraInputType ?? "text"}
                {...(props.extraPlaceholder !== undefined
                  ? { placeholder: props.extraPlaceholder }
                  : {})}
                value={edit.values.extra}
                disabled={saving}
                onChange={(e) =>
                  setEdit({ ...edit, values: { ...edit.values, extra: e.target.value } })
                }
              />
            </Field>

            <SkuTableEditor
              rows={edit.values.skus}
              errors={formErrors.skus}
              disabled={saving}
              onChange={(skus) => setEdit({ ...edit, values: { ...edit.values, skus } })}
            />

            {formError !== null ? (
              <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, fontWeight: 600 }}>
                {formError}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title="Remover cadastro"
        width={420}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveTarget(null)}>
              Cancelar
            </Button>
            <Button disabled={removing} onClick={() => void handleRemove()}>
              {removing ? "Removendo…" : "Remover"}
            </Button>
          </>
        }
      >
        {removeTarget !== null ? (
          <p style={{ fontSize: 13 }}>
            Remover <strong>{props.getName(removeTarget)}</strong>? Esta ação não
            pode ser desfeita.
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
